import axios from "axios";
import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage, setDataOnLocalStorage } from "../util/localStorage";
import { authStore } from "../util/authStore";
import { can, isPlatformAdmin } from "../util/accessControl";
import { chatbotHttp } from "./chatbotApi";

// Recovery and OTP requests must not inherit a stale session or client scope.
const api = axios.create({
  baseURL: process.env.REACT_APP_CHATBOT_API_URL || baseUrl,
  headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
});
const data = (response) => {
  const body = response.data;
  if (body?.status >= 400) throw new Error(body.message || "Authentication request failed.");
  return body?.data;
};
const authenticated = () => {
  const token = getDataFromLocalStorage("token");
  if (!token) throw new Error("Please sign in again.");
  return { headers: { Authorization: `Bearer ${token}` } };
};
export const accountAuthError = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || "Invalid input").join("; ");
  return error?.response?.data?.message || error?.message || "Unable to complete the request.";
};
export const getAuthMethods = () => api.get("/users/auth-methods").then(data);
export const passwordLogin = (email, password) =>
  api.post("/users/login", { source: "credentials", email, password }).then(data);
export const bootstrapAdmin = (values) => api.post("/users/bootstrap", values).then(data);
export const forgotPassword = (email) => api.post("/users/forgot-password", { email }).then(data);
export const completePassword = (purpose, values) => api.post(`/users/${purpose === "setup" ? "set-password" : "reset-password"}`, values).then(data);
export const changePassword = (values) => api.post("/users/me/change-password", values, authenticated()).then(data);
export const getAccountSecurity = () => api.get("/users/me/security", authenticated()).then(data);
export const resendInvitation = (userId) => api.post(`/users/${encodeURIComponent(userId)}/resend-invitation`, {}, authenticated()).then(data);
export const requestWhatsAppLogin = (phone) => api.post("/users/whatsapp/login/request", { phone }).then(data);
export const verifyWhatsAppLogin = (values) => api.post("/users/whatsapp/login/verify", values).then(data);
export const requestWhatsAppEnrollment = (values) => api.post("/users/me/whatsapp/enrollment/request", values, authenticated()).then(data);
export const verifyWhatsAppEnrollment = (values) => api.post("/users/me/whatsapp/enrollment/verify", values, authenticated()).then(data);
export const removeWhatsAppLogin = (current_password) => api.delete("/users/me/whatsapp", { ...authenticated(), data: { current_password } }).then(data);
export async function finishAccountLogin(result) {
  if (typeof result?.access_token !== "string" || !result.access_token) throw new Error("Unable to verify your session.");
  setDataOnLocalStorage("token", result.access_token);
  try {
    const user = await authStore.refresh(null);
    if (!user) throw new Error("Unable to verify your account.");
    if (user.must_change_password) return "/account/security";
    if (isPlatformAdmin(user)) return "/clients";
    if (can(user, "customers.read") && can(user, "appointments.read")) {
      const response = await chatbotHttp.get("/bots", { params: { page: 1, per_page: 2 } });
      const page = response.data?.data;
      if (!Array.isArray(page?.items) || !Number.isInteger(page.total) || page.total < 0
        || (page.total === 1 && (page.items.length !== 1 || !page.items[0]?.id))) {
        throw new Error("Unable to verify your accessible agents.");
      }
      if (page.total === 1) {
        return `/agents/${encodeURIComponent(page.items[0].id)}/customers?tab=appointments`;
      }
    }
    return "/agents";
  } catch (error) {
    authStore.logout();
    throw error;
  }
}
